'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const faqs = [
  {
    q: 'What is counselling?',
    a: 'Counselling is a talking therapy that provides a safe, confidential space for you to explore your thoughts, feelings, and experiences. A counsellor listens without judgement and helps you gain insight and develop strategies to manage difficulties.',
  },
  {
    q: 'How many sessions will I need?',
    a: 'This varies from person to person. Some people find that 6–12 sessions are sufficient, while others benefit from longer-term work. We will regularly review your progress together and you are always in control of how long we work together.',
  },
  {
    q: 'What happens in the first session?',
    a: 'The first session is an opportunity for us to meet, for you to share what has brought you to counselling, and for me to understand your needs. We will also discuss how we will work together and answer any questions you have. There is no pressure to share more than you feel comfortable with.',
  },
  {
    q: 'Is everything confidential?',
    a: 'Yes. Everything you share with me is held in strict confidence. The only exceptions are if I have serious concerns about your safety or the safety of others, in which case I may need to break confidentiality — but I would always discuss this with you first wherever possible.',
  },
  {
    q: 'How much does it cost?',
    a: 'Sessions are £55 for 50 minutes. An initial consultation is free for 20 minutes. I also offer a block of 6 sessions for £300, saving you £30. A limited number of reduced-rate places are available for those on low incomes.',
  },
  {
    q: 'Do you offer online sessions?',
    a: 'Yes. I offer both in-person and online sessions via a secure video platform. Online sessions are just as effective as face-to-face sessions and offer greater flexibility, particularly for those with busy schedules or who live further away.',
  },
  {
    q: 'What is the difference between counselling and psychotherapy?',
    a: 'Counselling typically focuses on specific issues and is often shorter-term, while psychotherapy tends to explore deeper patterns and may be longer-term. In practice, the two overlap considerably. As an integrative practitioner, I draw on both approaches depending on your needs.',
  },
  {
    q: 'How do I know if counselling is right for me?',
    a: 'If you are struggling with your mental health, relationships, or life circumstances, counselling can help. You do not need to be in crisis to benefit — many people come to counselling for personal growth and self-understanding. If you are unsure, please get in touch for an initial conversation.',
  },
  {
    q: 'What if I need to cancel a session?',
    a: 'I ask for at least 48 hours notice if you need to cancel or reschedule a session. Cancellations with less than 48 hours notice may be charged at the full session rate. I understand that emergencies happen and will always try to be flexible where possible.',
  },
  {
    q: 'Are you registered with a professional body?',
    a: 'Yes. I am a registered member of the British Association for Counselling and Psychotherapy (BACP) and adhere to their Ethical Framework for the Counselling Professions. I am also fully insured and committed to ongoing professional development and supervision.',
  },
];

export default function FAQsPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen">
      <Header />
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1B4D4A] to-[#2A6B67] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-3" style={{color: '#99C5C2'}}>Got Questions?</p>
          <h1 className="font-serif text-5xl text-white mb-4" style={{fontFamily: 'Georgia, serif'}}>Frequently Asked Questions</h1>
          <p className="text-white/80 text-base">
            <Link href="/" className="hover:text-white">Home</Link> <span className="mx-2">/</span> FAQs
          </p>
        </div>
      </section>
      {/* FAQs */}
      <section className="bg-white pt-12 pb-8">
        <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-2">
            {faqs?.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-[#F5F5F3] transition-colors"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                >
                  <span className="font-medium text-gray-800 text-base pr-4">{faq?.q}</span>
                  <svg
                    className={`w-5 h-5 text-[#1B4D4A] flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>
                {openIndex === i && (
                  <div className="px-6 pb-5 bg-[#F5F5F3]">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq?.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="bg-[#1B4D4A] py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl text-white mb-5" style={{fontFamily: 'Georgia, serif'}}>Still Have Questions?</h2>
          <p className="text-white/80 text-base mb-8">
            If you have a question that is not answered here, please do not hesitate to get in touch. I am happy to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/contact" className="bg-white text-[#1B4D4A] px-8 py-3.5 rounded-lg text-base font-medium hover:bg-gray-100 transition-colors">
              Contact Me
            </Link>
            <Link href="/book" className="border-2 border-white text-white px-8 py-3.5 rounded-lg text-base font-medium hover:bg-white hover:text-[#1B4D4A] transition-all">
              Book a Session
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
